<script setup>
import AdminLayout from "@/Layouts/AdminLayout/AdminLayout.vue"
import { computed, onMounted, ref } from "vue"
import { usePage, Link, router } from "@inertiajs/vue3"
import {
    Chart,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js'

Chart.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
)

const props = defineProps({
    stats: {
        type: Object,
        required: true
    },
    recentPairings: {
        type: Array,
        default: () => []
    },
    topEarners: {
        type: Array,
        default: () => []
    },
    weeklyPairings: {
        type: Array,
        default: () => []
    },
    monthlyRevenue: {
        type: Array,
        default: () => []
    },
    legDistribution: {
        type: Object,
        default: () => ({})
    },
    growthTrend: {
        type: Array,
        default: () => []
    },
    settings: {
        type: Object,
        default: () => ({})
    },
    systemHealth: {
        type: Object,
        default: () => ({})
    },
    performanceMetrics: {
        type: Object,
        default: () => ({})
    },
    isEnabled: {
        type: Boolean,
        default: false
    }
})

const page = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

// Chart instances
let weeklyPairingsChart = null
let monthlyRevenueChart = null
let legDistributionChart = null
let growthTrendChart = null

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('en-US').format(value)
}

const getRelativeTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString()
}

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?'
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)
}

// Computed stats
const balancePercentage = computed(() => {
    const total = props.stats.total_left_volume + props.stats.total_right_volume
    if (total === 0) return 50
    return (props.stats.total_left_volume / total) * 100
})

// Initialize charts
const initWeeklyPairingsChart = () => {
    try {
        const canvas = document.getElementById('weeklyPairingsChart')
        if (!canvas) return
        
        const ctx = canvas.getContext('2d')
        if (weeklyPairingsChart) {
            weeklyPairingsChart.destroy()
        }
        
        weeklyPairingsChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: props.weeklyPairings.map(d => d.date),
                datasets: [
                    {
                        label: 'Pairs Count',
                        data: props.weeklyPairings.map(d => d.pairs),
                        borderColor: 'rgb(59, 130, 246)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.4,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Rewards ($)',
                        data: props.weeklyPairings.map(d => d.rewards),
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        tension: 0.4,
                        fill: true,
                        yAxisID: 'y1',
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            boxWidth: 6
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        borderWidth: 1
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Pairs'
                        }
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Rewards ($)'
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                }
            }
        })
    } catch (error) {
        console.error('Failed to initialize weekly pairings chart:', error)
    }
}

const initMonthlyRevenueChart = () => {
    try {
        const canvas = document.getElementById('monthlyRevenueChart')
        if (!canvas) return
        
        const ctx = canvas.getContext('2d')
        if (monthlyRevenueChart) {
            monthlyRevenueChart.destroy()
        }
        
        monthlyRevenueChart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: props.monthlyRevenue.map(d => d.month),
                datasets: [{
                    label: 'Revenue',
                    data: props.monthlyRevenue.map(d => d.revenue),
                    backgroundColor: 'rgba(139, 92, 246, 0.8)',
                    borderColor: 'rgb(139, 92, 246)',
                    borderWidth: 1,
                    borderRadius: 8,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        callbacks: {
                            label: function(context) {
                                return 'Revenue: $' + context.parsed.y.toFixed(2)
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: function(value) {
                                return '$' + value.toFixed(0)
                            }
                        }
                    }
                }
            }
        })
    } catch (error) {
        console.error('Failed to initialize monthly revenue chart:', error)
    }
}

const initLegDistributionChart = () => {
    try {
        const canvas = document.getElementById('legDistributionChart')
        if (!canvas) return
        
        const ctx = canvas.getContext('2d')
        if (legDistributionChart) {
            legDistributionChart.destroy()
        }
        
        legDistributionChart = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Left Leg', 'Right Leg'],
                datasets: [{
                    data: [
                        props.legDistribution.left_volume || 0,
                        props.legDistribution.right_volume || 0
                    ],
                    backgroundColor: [
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(249, 115, 22, 0.8)'
                    ],
                    borderColor: [
                        'rgb(59, 130, 246)',
                        'rgb(249, 115, 22)'
                    ],
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            padding: 20,
                            usePointStyle: true
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        callbacks: {
                            label: function(context) {
                                const value = context.parsed
                                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                const percentage = ((value / total) * 100).toFixed(1)
                                return context.label + ': ' + value.toFixed(2) + ' (' + percentage + '%)'
                            }
                        }
                    }
                }
            }
        })
    } catch (error) {
        console.error('Failed to initialize leg distribution chart:', error)
    }
}

const initGrowthTrendChart = () => {
    try {
        const canvas = document.getElementById('growthTrendChart')
        if (!canvas) return
        
        const ctx = canvas.getContext('2d')
        if (growthTrendChart) {
            growthTrendChart.destroy()
        }
        
        growthTrendChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: props.growthTrend.map(d => d.date),
                datasets: [{
                    label: 'New Users',
                    data: props.growthTrend.map(d => d.users),
                    borderColor: 'rgb(236, 72, 153)',
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: 0,
                    pointHoverRadius: 6
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    },
                    x: {
                        ticks: {
                            maxTicksLimit: 10
                        }
                    }
                }
            }
        })
    } catch (error) {
        console.error('Failed to initialize growth trend chart:', error)
    }
}

// State management
const showSettingsModal = ref(false)
const isTogglingSystem = ref(false)
const showExportModal = ref(false)
const dateRange = ref('7days')

// Utility functions
const getTrendColor = (value) => {
    if (value > 0) return 'text-green-600 dark:text-green-400'
    if (value < 0) return 'text-red-600 dark:text-red-400'
    return 'text-gray-600 dark:text-gray-400'
}

const getTrendIcon = (value) => {
    if (value > 0) return '↑'
    if (value < 0) return '↓'
    return '→'
}

const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400'
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400'
    if (score >= 40) return 'text-orange-600 dark:text-orange-400'
    return 'text-red-600 dark:text-red-400'
}

const getHealthBgColor = (score) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    if (score >= 40) return 'bg-orange-500'
    return 'bg-red-500'
}

// Actions
const toggleSystem = () => {
    if (isTogglingSystem.value) return
    isTogglingSystem.value = true
    
    router.post('/admin/binary/toggle-system', {
        enabled: !props.isEnabled
    }, {
        onFinish: () => {
            isTogglingSystem.value = false
        }
    })
}

const exportData = () => {
    window.location.href = '/admin/binary/export?format=csv'
}

onMounted(() => {
    initWeeklyPairingsChart()
    initMonthlyRevenueChart()
    initLegDistributionChart()
    initGrowthTrendChart()
})
</script>

<template>
    <AdminLayout title="Binary MLM Dashboard">
        <div class="space-y-6">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                            Binary MLM Dashboard
                        </h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Comprehensive analytics and performance metrics
                        </p>
                    </div>
                        <div class="flex items-center space-x-3">
                            <!-- Export Button -->
                            <button @click="exportData" class="px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-2 shadow-sm">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <span class="font-medium">Export</span>
                            </button>
                            
                            <!-- System Toggle -->
                            <button @click="toggleSystem" :disabled="isTogglingSystem" 
                                class="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 shadow-lg transition-all duration-300"
                                :class="isEnabled ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'">
                                <div class="w-2 h-2 rounded-full animate-pulse" :class="isEnabled ? 'bg-white' : 'bg-white'"></div>
                                <span>{{ isEnabled ? 'System Active' : 'System Inactive' }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- System Health & Performance Bar -->
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <!-- System Health -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">System Health</h3>
                            <div class="px-3 py-1 rounded-full text-xs font-bold" :class="systemHealth.overall_score >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 
                                     systemHealth.overall_score >= 60 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 
                                     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'">
                                {{ systemHealth.overall_score }}%
                            </div>
                        </div>
                        <div class="space-y-3">
                            <div class="flex items-center justify-between text-sm">
                                <span class="text-gray-600 dark:text-gray-400">Active Rate</span>
                                <span :class="getHealthColor(systemHealth.active_rate)" class="font-semibold">{{ systemHealth.active_rate }}%</span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                <div class="h-2 rounded-full transition-all duration-500" :class="getHealthBgColor(systemHealth.active_rate)" :style="{ width: systemHealth.active_rate + '%' }"></div>
                            </div>
                            
                            <div class="flex items-center justify-between text-sm">
                                <span class="text-gray-600 dark:text-gray-400">Balance Health</span>
                                <span :class="getHealthColor(systemHealth.balance_health)" class="font-semibold">{{ systemHealth.balance_health }}%</span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                <div class="h-2 rounded-full transition-all duration-500" :class="getHealthBgColor(systemHealth.balance_health)" :style="{ width: systemHealth.balance_health + '%' }"></div>
                            </div>
                            
                            <div class="flex items-center justify-between text-sm">
                                <span class="text-gray-600 dark:text-gray-400">Activity Health</span>
                                <span :class="getHealthColor(systemHealth.activity_health)" class="font-semibold">{{ systemHealth.activity_health }}%</span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                <div class="h-2 rounded-full transition-all duration-500" :class="getHealthBgColor(systemHealth.activity_health)" :style="{ width: systemHealth.activity_health + '%' }"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Performance Metrics -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">Performance Trends</h3>
                        <div class="space-y-4">
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">Pairs Today</p>
                                    <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(performanceMetrics.today_pairs) }}</p>
                                </div>
                                <div class="flex items-center space-x-1" :class="getTrendColor(performanceMetrics.pairs_trend)">
                                    <span class="text-2xl font-bold">{{ getTrendIcon(performanceMetrics.pairs_trend) }}</span>
                                    <span class="text-sm font-bold">{{ Math.abs(performanceMetrics.pairs_trend) }}%</span>
                                </div>
                            </div>
                            
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">Revenue Today</p>
                                    <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ currencySymbol }}{{ formatCurrency(performanceMetrics.today_revenue) }}</p>
                                </div>
                                <div class="flex items-center space-x-1" :class="getTrendColor(performanceMetrics.revenue_trend)">
                                    <span class="text-2xl font-bold">{{ getTrendIcon(performanceMetrics.revenue_trend) }}</span>
                                    <span class="text-sm font-bold">{{ Math.abs(performanceMetrics.revenue_trend) }}%</span>
                                </div>
                            </div>
                            
                            <div class="flex items-center justify-between">
                                <div>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">User Growth</p>
                                    <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(performanceMetrics.this_week_users) }}/week</p>
                                </div>
                                <div class="flex items-center space-x-1" :class="getTrendColor(performanceMetrics.user_growth_trend)">
                                    <span class="text-2xl font-bold">{{ getTrendIcon(performanceMetrics.user_growth_trend) }}</span>
                                    <span class="text-sm font-bold">{{ Math.abs(performanceMetrics.user_growth_trend) }}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Quick Settings -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">Quick Settings</h3>
                        <div class="space-y-3">
                            <div class="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <span class="text-sm text-gray-700 dark:text-gray-300">Daily Limit</span>
                                <span class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ settings.daily_pairing_limit }} pairs</span>
                            </div>
                            <div class="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <span class="text-sm text-gray-700 dark:text-gray-300">Bonus Rate</span>
                                <span class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ settings.pairing_bonus_percentage }}%</span>
                            </div>
                            <div class="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <span class="text-sm text-gray-700 dark:text-gray-300">USDT / Credit Split</span>
                                <span class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ settings.invest_usdt_percentage }}% / {{ settings.invest_credit_percentage }}%</span>
                            </div>
                            <div class="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <span class="text-sm text-gray-700 dark:text-gray-300">Lock Days</span>
                                <span class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ settings.token_lock_days }} days</span>
                            </div>
                        </div>
                        <Link href="/admin/settings#binary" class="mt-4 w-full flex items-center justify-center px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                            Manage Settings
                        </Link>
                    </div>
                </div>

                <!-- Main Stats Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <!-- Total Users in Binary -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-4">
                                <div class="p-3 bg-blue-500 rounded-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                    </svg>
                                </div>
                                <span class="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-200 dark:bg-blue-900/50 px-3 py-1 rounded-full">
                                    +{{ formatNumber(stats.active_users_today) }} today
                                </span>
                            </div>
                            <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                                {{ formatNumber(stats.total_users) }}
                            </h3>
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users in Binary</p>
                            <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">
                                {{ formatNumber(stats.total_users_unplaced) }} unplaced users
                            </div>
                    </div>

                    <!-- Pairings Today -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-4">
                                <div class="p-3 bg-green-500 rounded-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div class="flex items-center space-x-1">
                                    <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    <span class="text-xs font-semibold text-green-600 dark:text-green-400">Live</span>
                                </div>
                            </div>
                            <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                                {{ formatNumber(stats.pairings_today) }}
                            </h3>
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Pairings Today</p>
                            <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">
                                {{ formatNumber(stats.total_pairings_all_time) }} total pairs
                            </div>
                    </div>

                    <!-- Total Bonuses Paid -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-4">
                                <div class="p-3 bg-purple-500 rounded-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                            </div>
                            <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                                {{ currencySymbol }}{{ formatCurrency(stats.total_bonuses_paid) }}
                            </h3>
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bonuses Paid</p>
                            <div class="mt-4 text-xs text-green-600 dark:text-green-400 font-semibold">
                                +{{ currencySymbol }}{{ formatCurrency(stats.total_rewards_paid_today) }} today
                            </div>
                    </div>

                    <!-- Active Packages -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-4">
                                <div class="p-3 bg-orange-500 rounded-lg">
                                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                                    </svg>
                                </div>
                            </div>
                            <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                                {{ formatNumber(stats.active_packages) }}
                            </h3>
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Active Packages</p>
                            <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">
                                Avg {{ stats.avg_daily_pairs ? stats.avg_daily_pairs.toFixed(1) : '0' }} pairs/day
                            </div>
                    </div>
                </div>

                <!-- Spec §9 Stats: Packages Sold · Tokens Locked · Credits Issued -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <!-- Total Packages Sold -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-4">
                            <div class="p-3 bg-indigo-500 rounded-lg">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                                </svg>
                            </div>
                        </div>
                        <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                            {{ formatNumber(stats.total_packages_sold) }}
                        </h3>
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Packages Sold</p>
                        <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">All active &amp; completed investments</div>
                    </div>

                    <!-- Total Tokens Locked -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-4">
                            <div class="p-3 bg-yellow-500 rounded-lg">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                </svg>
                            </div>
                        </div>
                        <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                            {{ currencySymbol }}{{ formatCurrency(stats.total_tokens_locked) }}
                        </h3>
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tokens Locked</p>
                        <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">Across all user wallets</div>
                    </div>

                    <!-- Total Credits Issued -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-4">
                            <div class="p-3 bg-teal-500 rounded-lg">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"></path>
                                </svg>
                            </div>
                        </div>
                        <h3 class="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-1">
                            {{ currencySymbol }}{{ formatCurrency(stats.total_credits_issued) }}
                        </h3>
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Credits Issued</p>
                        <div class="mt-4 text-xs text-gray-500 dark:text-gray-500">Current credit balance across all users</div>
                    </div>
                </div>

                <!-- Charts Section -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <!-- Weekly Pairings Chart -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">Weekly Pairings & Rewards</h3>
                        <div class="h-64">
                            <canvas id="weeklyPairingsChart"></canvas>
                        </div>
                    </div>

                    <!-- Leg Distribution Chart -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">Leg Volume Distribution</h3>
                        <div class="h-64 flex items-center justify-center">
                            <canvas id="legDistributionChart"></canvas>
                        </div>
                        <div class="mt-4 grid grid-cols-2 gap-4 text-sm">
                            <div class="text-center">
                                <p class="text-gray-600 dark:text-gray-400">Left Carry Forward</p>
                                <p class="text-lg font-bold text-blue-600 dark:text-blue-400">
                                    {{ formatCurrency(legDistribution.left_carry_forward) }}
                                </p>
                            </div>
                            <div class="text-center">
                                <p class="text-gray-600 dark:text-gray-400">Right Carry Forward</p>
                                <p class="text-lg font-bold text-orange-600 dark:text-orange-400">
                                    {{ formatCurrency(legDistribution.right_carry_forward) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Additional Charts -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <!-- Monthly Revenue Chart -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">Monthly Revenue Trend (12 Months)</h3>
                        <div class="h-64">
                            <canvas id="monthlyRevenueChart"></canvas>
                        </div>
                    </div>

                    <!-- Growth Trend Chart -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">User Growth Trend (30 Days)</h3>
                        <div class="h-64">
                            <canvas id="growthTrendChart"></canvas>
                        </div>
                    </div>
                </div>

                <!-- Recent Activity & Top Performers -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <!-- Recent Pairings -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <div class="p-6 border-b border-gray-200 dark:border-slate-700">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Recent Pairings</h3>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Latest pairing activities</p>
                                </div>
                                <Link href="/admin/binary/pairings" class="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                                    View All
                                </Link>
                            </div>
                        </div>
                        <div class="p-6">
                            <div v-if="recentPairings.length === 0" class="text-center py-12">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                                </svg>
                                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No recent pairings</p>
                            </div>
                            <div v-else class="space-y-3">
                                <div v-for="pairing in recentPairings" :key="pairing.id" 
                                    class="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:shadow-sm transition-shadow duration-200 border border-gray-200 dark:border-slate-600">
                                    <div class="flex items-center space-x-4">
                                        <div class="flex-shrink-0">
                                            <div class="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                                <span class="text-sm font-bold text-white">
                                                    {{ getInitials(pairing.user_name) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {{ pairing.user_name }}
                                            </p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                                {{ getRelativeTime(pairing.created_at) }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="text-right">
                                        <p class="text-sm font-bold text-green-600 dark:text-green-400">
                                            {{ currencySymbol }}{{ formatCurrency(pairing.reward_amount) }}
                                        </p>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            <span class="inline-flex items-center space-x-1">
                                                <span class="text-blue-600 dark:text-blue-400">{{ formatNumber(pairing.left_volume) }}</span>
                                                <span>:</span>
                                                <span class="text-orange-600 dark:text-orange-400">{{ formatNumber(pairing.right_volume) }}</span>
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Top Earners -->
                    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <div class="p-6 border-b border-gray-200 dark:border-slate-700">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Top Earners</h3>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Highest earning members</p>
                                </div>
                                <Link href="/admin/binary/users" class="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                                    View All
                                </Link>
                            </div>
                        </div>
                        <div class="p-6">
                            <div v-if="topEarners.length === 0" class="text-center py-12">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
                                </svg>
                                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No data available</p>
                            </div>
                            <div v-else class="space-y-3">
                                <div v-for="(earner, index) in topEarners" :key="earner.id" 
                                    class="flex items-center justify-between p-4 rounded-lg hover:shadow-sm transition-shadow duration-200 border"
                                    :class="index === 0 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700' : 
                                            index === 1 ? 'bg-gray-50 dark:bg-gray-700/20 border-gray-200 dark:border-gray-600' : 
                                            index === 2 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700' : 
                                            'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600'">
                                    <div class="flex items-center space-x-4">
                                        <div class="flex-shrink-0">
                                            <div class="w-12 h-12 rounded-lg flex items-center justify-center"
                                                :class="index === 0 ? 'bg-yellow-500' : 
                                                        index === 1 ? 'bg-gray-400' : 
                                                        index === 2 ? 'bg-orange-500' : 
                                                        'bg-blue-500'">
                                                <span class="text-lg font-bold text-white">
                                                    {{ index + 1 }}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {{ earner.display_name || earner.name }}
                                            </p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                                {{ earner.email }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="text-right">
                                        <p class="text-sm font-bold text-green-600 dark:text-green-400">
                                            {{ currencySymbol }}{{ formatCurrency(earner.total_binary_bonus) }}
                                        </p>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {{ formatNumber(earner.total_pairs) }} pairs
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Actions -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link href="/admin/binary/tree" class="block p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center space-x-4">
                            <div class="p-3 bg-blue-500 rounded-lg">
                                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                                </svg>
                            </div>
                            <div>
                                <h4 class="text-lg font-bold text-gray-900 dark:text-gray-100">View Binary Tree</h4>
                                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">Visualize network structure</p>
                            </div>
                        </div>
                    </Link>

                    <Link href="/admin/binary/users" class="block p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center space-x-4">
                            <div class="p-3 bg-green-500 rounded-lg">
                                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
                                </svg>
                            </div>
                            <div>
                                <h4 class="text-lg font-bold text-gray-900 dark:text-gray-100">Manage Users</h4>
                                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">Place and manage users</p>
                            </div>
                        </div>
                    </Link>

                    <Link href="/admin/binary/pairings" class="block p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center space-x-4">
                            <div class="p-3 bg-purple-500 rounded-lg">
                                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                                </svg>
                            </div>
                            <div>
                                <h4 class="text-lg font-bold text-gray-900 dark:text-gray-100">Pairing History</h4>
                                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">View all pairings</p>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
    </AdminLayout>
</template>
